import React, { useEffect, useState, useRef, memo, Component } from 'react';
import { AlertCircle, CheckCircle, ChevronRight, RefreshCw, Trophy, Star, CheckCircle2, TrendingUp, Menu, X, Globe, Play, LogOut, ChevronDown, Search, Volume2, VolumeX } from 'lucide-react';
// Types
type Question = {
  id: number;
  text: string;
  options: string[];
  correctAnswer: number; // index
  backgroundImage: string;
  correctMessage: string;
  incorrectMessage: string;
  voiceoverUrl?: string;
};
// Shuffle utility
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
function shuffleQuestionOptions(qs: Question[]): Question[] {
  return qs.map(q => {
    const indices = q.options.map((_, i) => i);
    const shuffledIndices = shuffleArray(indices);
    const shuffledOptions = shuffledIndices.map(i => q.options[i]);
    const newCorrectAnswer = shuffledIndices.indexOf(q.correctAnswer);
    return {
      ...q,
      options: shuffledOptions,
      correctAnswer: newCorrectAnswer
    };
  });
}
// Data
const questions: Question[] = [{
  id: 1,
  text: "You're conducting a routine fire safety inspection. What's your objective for this walkthrough?",
  options: ['Identify and control anything that could delay evacuation or fuel a fire', 'Check only visible equipment and move on quickly', 'Focus on paperwork, not physical conditions', 'Wait for the alarm test to confirm everything works'],
  correctAnswer: 0,
  backgroundImage: 'https://cdn.magicpatterns.com/uploads/9aAWsRK7d5PvLBzqYRzvLo/0.png',
  correctMessage: 'Correct. Your goal is to prevent conditions that cause fires or slow evacuation.',
  incorrectMessage: 'Not quite. The inspection is about reducing fire risk and keeping escape routes usable.',
  voiceoverUrl: 'https://res.cloudinary.com/dlwyytzg3/video/upload/v1770748360/1_li0h6s.mp3'
}, {
  id: 2,
  text: 'What should you check first along an escape route?',
  options: ['That the route is clear and usable end-to-end', 'That the corridor looks clean and tidy', 'That people know the route by memory', 'That the route has posters and reminders'],
  correctAnswer: 0,
  backgroundImage: 'https://cdn.magicpatterns.com/uploads/r29nGHKCSzS1QNsMRWcSEG/1.png',
  correctMessage: "Correct. If the route isn't clear, everything else becomes harder during evacuation.",
  incorrectMessage: 'Not quite. Escape routes must be clear and usable at the moment people need them.',
  voiceoverUrl: 'https://res.cloudinary.com/dlwyytzg3/video/upload/v1770748361/2_rtxpgr.mp3'
}, {
  id: 3,
  text: 'What tells you this extinguisher is ready for use?',
  options: ['Pressure gauge is in the green and the pin/seal is intact', "It's mounted on the wall at eye level", 'It has a red body and a label', "It's near a door"],
  correctAnswer: 0,
  backgroundImage: 'https://cdn.magicpatterns.com/uploads/kN5F5357wudz59YYqEankb/2.png',
  correctMessage: 'Correct. Gauge in the green plus pin/seal intact indicates readiness.',
  incorrectMessage: 'Not quite. Location helps, but readiness is confirmed by condition checks.',
  voiceoverUrl: 'https://res.cloudinary.com/dlwyytzg3/video/upload/v1770748361/3_xetmis.mp3'
}, {
  id: 4,
  text: "You notice a fire door wedged open. What's the correct action?",
  options: ['Remove the wedge and ensure the door closes fully', 'Leave it open to reduce congestion', 'Prop it wider so people can move faster', 'Ignore it if the corridor is quiet'],
  correctAnswer: 0,
  backgroundImage: 'https://cdn.magicpatterns.com/uploads/keYY1WaJVf5YUHTMAXnSFb/3.png',
  correctMessage: 'Correct. Fire doors must close to slow smoke and fire spread.',
  incorrectMessage: 'Not quite. A wedged fire door can let smoke spread into escape routes.',
  voiceoverUrl: 'https://res.cloudinary.com/dlwyytzg3/video/upload/v1770748362/4_rzo9dx.mp3'
}, {
  id: 5,
  text: 'A distraction pulls your attention away. What do you do before moving on?',
  options: ['Finish the safety-critical action you started, then respond', 'Respond immediately and come back later', 'Assume someone else will handle it', 'Skip it to stay on schedule'],
  correctAnswer: 0,
  backgroundImage: 'https://cdn.magicpatterns.com/uploads/aedEjemySaaxYUvkQFVnjp/5.png',
  correctMessage: "Correct. Complete the control while you're there. Small delays become missed controls.",
  incorrectMessage: 'Not quite. Distractions are where critical misses happen.',
  voiceoverUrl: 'https://res.cloudinary.com/dlwyytzg3/video/upload/v1770748362/5_armgvd.mp3'
}, {
  id: 6,
  text: 'Is this exit route acceptable as it is right now?',
  options: ['Yes, as long as people can squeeze past', 'Yes, if the door can still open', 'No, any obstruction can slow evacuation and cause bottlenecks', 'No, only if it blocks the entire corridor'],
  correctAnswer: 2,
  backgroundImage: 'https://cdn.magicpatterns.com/uploads/mYjUZCDPGhgLNHSrbvLqan/6.png',
  correctMessage: 'Correct. Partial blockage still creates crowding, delays, and injury risk.',
  incorrectMessage: 'Not quite. Even small obstructions become serious when visibility drops and people are moving fast.',
  voiceoverUrl: 'https://res.cloudinary.com/dlwyytzg3/video/upload/v1770748363/6_mcjrqo.mp3'
}, {
  id: 7,
  text: "What's the most important fire risk you spot immediately?",
  options: ['Combustibles too close to a heat source', 'The kitchen is busy', 'The lighting is dim', 'There are too many appliances in the room'],
  correctAnswer: 0,
  backgroundImage: 'https://cdn.magicpatterns.com/uploads/6nXyoXhCR2mWsHty74GEtk/7.png',
  correctMessage: 'Correct. Combustibles near heat sources are a common ignition path.',
  incorrectMessage: 'Not quite. Focus on what can ignite quickly and spread.',
  voiceoverUrl: 'https://res.cloudinary.com/dlwyytzg3/video/upload/v1770748364/7_rzpoy8.mp3'
}, {
  id: 8,
  text: 'A fire has started. What is your first priority?',
  options: ['Raise the alarm and start evacuation', 'Find the nearest extinguisher and fight the fire no matter what', 'Open windows to clear smoke', 'Collect your inspection notes'],
  correctAnswer: 0,
  backgroundImage: 'https://cdn.magicpatterns.com/uploads/688FeRsjzuqMtQmcMrnEYa/8.png',
  correctMessage: 'Correct. Alarm and evacuation come first. Time is critical.',
  incorrectMessage: "Not quite. If people aren't alerted and moving, the risk escalates fast.",
  voiceoverUrl: 'https://res.cloudinary.com/dlwyytzg3/video/upload/v1770748365/8_eu1gip.mp3'
}, {
  id: 9,
  text: 'The alarm is sounding. What instruction do you give people right now?',
  options: ['"Stay where you are until someone comes."', '"Leave immediately using the nearest safe exit, follow signage."', '"Finish what you\'re doing then go."', '"Go to the elevator lobby for updates."'],
  correctAnswer: 1,
  backgroundImage: 'https://cdn.magicpatterns.com/uploads/niUBqw362GiNcWEuMLcqrt/9.png',
  correctMessage: 'Correct. Clear, immediate direction reduces hesitation.',
  incorrectMessage: 'Not quite. The instruction must be simple, immediate, and evacuation-focused.',
  voiceoverUrl: 'https://res.cloudinary.com/dlwyytzg3/video/upload/v1770748366/9_ib8iws.mp3'
}, {
  id: 10,
  text: 'People are heading to the elevator. What do you tell them?',
  options: ['"Use the elevator if you\'re in a hurry."', '"Use the stairs, do not use the elevator during a fire alarm."', '"Wait and see if the alarm stops."', '"Split up: half elevator, half stairs."'],
  correctAnswer: 1,
  backgroundImage: 'https://cdn.magicpatterns.com/uploads/fpdSsn7wU9HrAkuiqwPNoB/10.png',
  correctMessage: 'Correct. Elevators can fail, open onto smoke, or stop at unsafe floors.',
  incorrectMessage: 'Not quite. Elevators are not reliable or safe during a fire event.',
  voiceoverUrl: 'https://res.cloudinary.com/dlwyytzg3/video/upload/v1770748367/10_nmfr5l.mp3'
}, {
  id: 11,
  text: "As you move through, what do you do with doors where it's safe to do so?",
  options: ['Leave doors open so people move faster', 'Close doors behind you to limit smoke spread', 'Lock doors so no one returns', 'Wedge doors open for airflow'],
  correctAnswer: 1,
  backgroundImage: 'https://cdn.magicpatterns.com/uploads/tG7sbzw7F3DvsbedKwPgro/11.png',
  correctMessage: 'Correct. Closing doors helps contain smoke and slows fire spread.',
  incorrectMessage: 'Not quite. Open doors can allow smoke to flood escape routes.',
  voiceoverUrl: 'https://res.cloudinary.com/dlwyytzg3/video/upload/v1770748367/11_vchxuf.mp3'
}, {
  id: 12,
  text: "Someone tries to go back inside. What's your response?",
  options: ['Let them in if they insist', 'Tell them to wait at the door until you check', 'Stop them, direct them back to the assembly point, and alert responders if someone is missing', 'Ignore them and keep moving'],
  correctAnswer: 2,
  backgroundImage: 'https://cdn.magicpatterns.com/uploads/dmaPdKqoWzTFufzGUQFrJZ/12.png',
  correctMessage: 'Correct. Prevent re-entry and escalate if someone may be inside.',
  incorrectMessage: 'Not quite. Re-entry during an active event is high risk and increases casualties.',
  voiceoverUrl: 'https://res.cloudinary.com/dlwyytzg3/video/upload/v1770748369/12_otwfht.mp3'
}, {
  id: 13,
  text: 'What early inspection miss is now creating the biggest life safety impact?',
  options: ["The extinguisher tag wasn't updated", 'The exit route obstruction is causing a bottleneck', 'The kitchen looked untidy', "The building map wasn't updated"],
  correctAnswer: 1,
  backgroundImage: 'https://cdn.magicpatterns.com/uploads/bajGvdBXRQAojN6to5n8di/13.png',
  correctMessage: 'Correct. Obstructions create crowding and delay, especially with smoke and low visibility.',
  incorrectMessage: 'Not quite. Look for what is actively slowing evacuation right now.',
  voiceoverUrl: 'https://res.cloudinary.com/dlwyytzg3/video/upload/v1770748370/13_fxhhua.mp3'
}, {
  id: 14,
  text: 'What single change would have most reduced risk in this event?',
  options: ['More posters about fire safety', 'Keeping escape routes and fire doors clear and functioning at all times', 'More frequent alarm drills only', 'Adding more kitchen appliances'],
  correctAnswer: 1,
  backgroundImage: 'https://cdn.magicpatterns.com/uploads/sZ9h9vUrSg9YYfDgGXMYz1/14.png',
  correctMessage: 'Correct. Maintaining core controls prevents predictable failures during real events.',
  incorrectMessage: 'Not quite. The biggest impact comes from removing conditions that block escape and accelerate smoke spread.',
  voiceoverUrl: 'https://res.cloudinary.com/dlwyytzg3/video/upload/v1770748371/14_jwsy7d.mp3'
}];
const languages = [{
  code: 'en',
  name: 'English'
}, {
  code: 'es',
  name: 'Español'
}, {
  code: 'fr',
  name: 'Français'
}, {
  code: 'de',
  name: 'Deutsch'
}, {
  code: 'ja',
  name: '日本語'
}, {
  code: 'zh',
  name: '中文'
}, {
  code: 'ko',
  name: '한국어'
}, {
  code: 'pt',
  name: 'Português'
}, {
  code: 'it',
  name: 'Italiano'
}, {
  code: 'ru',
  name: 'Русский'
}, {
  code: 'ar',
  name: 'العربية'
}, {
  code: 'hi',
  name: 'हिन्दी'
}, {
  code: 'nl',
  name: 'Nederlands'
}, {
  code: 'pl',
  name: 'Polski'
}, {
  code: 'sv',
  name: 'Svenska'
}, {
  code: 'tr',
  name: 'Türkçe'
}, {
  code: 'vi',
  name: 'Tiếng Việt'
}, {
  code: 'th',
  name: 'ไทย'
}, {
  code: 'id',
  name: 'Bahasa Indonesia'
}, {
  code: 'cs',
  name: 'Čeština'
}, {
  code: 'el',
  name: 'Ελληνικά'
}, {
  code: 'he',
  name: 'עברית'
}, {
  code: 'da',
  name: 'Dansk'
}, {
  code: 'fi',
  name: 'Suomi'
}, {
  code: 'no',
  name: 'Norsk'
}];
// Confirmation Dialog Component
type DialogConfig = {
  title: string;
  description?: string;
  actions: {
    label: string;
    onClick: () => void;
    variant: 'primary' | 'destructive' | 'cancel';
  }[];
};
function ConfirmationDialog({
  config,
  onClose
}: {
  config: DialogConfig;
  onClose: () => void;
}) {
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-0">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose} data-id="element-1" />
      <div className="relative w-full max-w-[280px] bg-zinc-900/95 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-2">
        <div className="px-6 pt-6 pb-4 text-center" data-id="element-3">
          <h3 className="text-lg font-semibold text-white mb-1" data-id="element-4">
            {config.title}
          </h3>
          {config.description && <p className="text-sm text-white/60 leading-relaxed" data-id="element-5">
              {config.description}
            </p>}
        </div>
        <div className="border-t border-white/10" data-id="element-6">
          {config.actions.map((action, index) => <button key={index} onClick={action.onClick} className={`
                w-full min-h-[44px] py-3 px-4 text-[17px] font-normal
                transition-colors
                ${index > 0 ? 'border-t border-white/10' : ''}
                ${action.variant === 'primary' ? 'text-white font-semibold hover:bg-white/10' : action.variant === 'destructive' ? 'text-red-400 hover:bg-white/10' : 'text-white/70 hover:bg-white/10'}
              `} data-id="element-7">
              {action.label}
            </button>)}
        </div>
      </div>
    </div>;
}
export function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [introFading, setIntroFading] = useState(false);
  const [shuffledQuestions, setShuffledQuestions] = useState<Question[]>(() => shuffleQuestionOptions(questions));
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [userAnswers, setUserAnswers] = useState<(number | null)[]>(new Array(questions.length).fill(null));
  const [isReviewing, setIsReviewing] = useState(false);
  const [animatedScore, setAnimatedScore] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [languageSearch, setLanguageSearch] = useState('');
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showRestartDialog, setShowRestartDialog] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  // Preload all assets on mount
  useEffect(() => {
    const startTime = Date.now();
    const MIN_DISPLAY = 2000;
    const MAX_WAIT = 6000;
    const preloadImage = (src: string): Promise<void> => new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });
    const preloadAudio = (src: string): Promise<void> => new Promise(resolve => {
      const audio = new Audio();
      audio.preload = 'auto';
      const done = () => resolve();
      audio.addEventListener('canplaythrough', done, {
        once: true
      });
      audio.addEventListener('error', done, {
        once: true
      });
      audio.src = src;
    });
    const imagePromises = questions.map(q => preloadImage(q.backgroundImage));
    const audioPromises = questions.filter(q => q.voiceoverUrl).map(q => preloadAudio(q.voiceoverUrl!));
    const logoPromise = preloadImage('https://cdn.magicpatterns.com/uploads/q3mHGwFqwjFEjCwMdRa798/Pixaera_Logo_White.png');
    const allAssets = Promise.all([...imagePromises, ...audioPromises, logoPromise]);
    const timeout = new Promise<void>(resolve => setTimeout(resolve, MAX_WAIT));
    Promise.race([allAssets, timeout]).then(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, MIN_DISPLAY - elapsed);
      setTimeout(() => {
        setIntroFading(true);
        setTimeout(() => setIsLoading(false), 800);
      }, remaining);
    });
  }, []);
  // Helper to play voiceover
  const playVoiceover = (url?: string) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsAudioPlaying(false);
    }
    if (url && !isMuted) {
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.addEventListener('ended', () => setIsAudioPlaying(false));
      audio.addEventListener('error', () => setIsAudioPlaying(false));
      audio.play().then(() => setIsAudioPlaying(true)).catch(() => setIsAudioPlaying(false));
    }
  };
  // Stop audio when changing questions
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsAudioPlaying(false);
    }
    // In review mode, auto-play voiceover since user already answered
    if (isReviewing && !showResults) {
      const q = shuffledQuestions[currentQuestionIndex];
      playVoiceover(q?.voiceoverUrl);
    }
  }, [currentQuestionIndex, showResults]);
  // Rolling prefetch: preload next 3 images + audio as safety net
  useEffect(() => {
    for (let i = 1; i <= 3; i++) {
      const next = currentQuestionIndex + i;
      if (next < shuffledQuestions.length) {
        const q = shuffledQuestions[next];
        const img = new Image();
        img.src = q.backgroundImage;
        if (q.voiceoverUrl) {
          const audio = new Audio();
          audio.preload = 'auto';
          audio.src = q.voiceoverUrl;
        }
      }
    }
  }, [currentQuestionIndex, shuffledQuestions]);
  // Handle mute toggle
  const handleToggleMute = () => {
    if (isMuted) {
      setIsMuted(false);
      // If already answered, replay the voiceover
      if ((isAnswered || isReviewing) && currentQuestion.voiceoverUrl && !showResults) {
        setTimeout(() => {
          const audio = new Audio(currentQuestion.voiceoverUrl!);
          audioRef.current = audio;
          audio.addEventListener('ended', () => setIsAudioPlaying(false));
          audio.addEventListener('error', () => setIsAudioPlaying(false));
          audio.play().then(() => setIsAudioPlaying(true)).catch(() => setIsAudioPlaying(false));
        }, 0);
      }
    } else {
      setIsMuted(true);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setIsAudioPlaying(false);
      }
    }
  };
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [currentQuestionIndex]);
  const filteredLanguages = languages.filter(lang => lang.name.toLowerCase().includes(languageSearch.toLowerCase()) || lang.code.toLowerCase().includes(languageSearch.toLowerCase()));
  useEffect(() => {
    if (isLanguageOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isLanguageOpen]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
        setIsLanguageOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const currentQuestion = shuffledQuestions[currentQuestionIndex];
  const scorePercentage = Math.round(score / shuffledQuestions.length * 100);
  useEffect(() => {
    if (showResults && !isReviewing) {
      let current = 0;
      const increment = score / 20;
      const timer = setInterval(() => {
        current += increment;
        if (current >= score) {
          setAnimatedScore(score);
          clearInterval(timer);
        } else {
          setAnimatedScore(Math.floor(current));
        }
      }, 30);
      return () => clearInterval(timer);
    }
  }, [showResults, score, isReviewing]);
  const getPerformanceTier = () => {
    if (scorePercentage === 100) return {
      icon: Trophy,
      title: 'Perfect Score!',
      message: 'Outstanding work! You got every question right.',
      color: 'from-yellow-500/20 to-orange-500/20',
      ring: 'ring-yellow-500/30',
      iconColor: 'text-yellow-400'
    };
    if (scorePercentage >= 80) return {
      icon: Star,
      title: 'Excellent!',
      message: 'Great job! You really know your stuff.',
      color: 'from-green-500/20 to-emerald-500/20',
      ring: 'ring-green-500/30',
      iconColor: 'text-green-400'
    };
    if (scorePercentage >= 60) return {
      icon: CheckCircle2,
      title: 'Good Work!',
      message: 'Nice effort! Keep practicing to improve.',
      color: 'from-blue-500/20 to-cyan-500/20',
      ring: 'ring-blue-500/30',
      iconColor: 'text-blue-400'
    };
    return {
      icon: TrendingUp,
      title: 'Keep Trying!',
      message: 'Practice makes perfect. Try again!',
      color: 'from-purple-500/20 to-pink-500/20',
      ring: 'ring-purple-500/30',
      iconColor: 'text-purple-400'
    };
  };
  const handleOptionSelect = (index: number) => {
    if (isAnswered && !isReviewing) return;
    if (isReviewing) return;
    setSelectedOption(index);
    setIsAnswered(true);
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestionIndex] = index;
    setUserAnswers(newAnswers);
    if (index === currentQuestion.correctAnswer) {
      setScore(s => s + 1);
    }
    // Play voiceover after answering
    playVoiceover(currentQuestion.voiceoverUrl);
  };
  const handleNext = () => {
    if (currentQuestionIndex < shuffledQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedOption(isReviewing ? userAnswers[currentQuestionIndex + 1] : null);
      setIsAnswered(isReviewing ? true : false);
    } else {
      if (isReviewing) {
        setIsReviewing(false);
        setShowResults(true);
      } else {
        setShowResults(true);
      }
    }
  };
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setSelectedOption(userAnswers[currentQuestionIndex - 1]);
      setIsAnswered(true);
    }
  };
  const handleReview = () => {
    setShowResults(false);
    setIsReviewing(true);
    setCurrentQuestionIndex(0);
    setSelectedOption(userAnswers[0]);
    setIsAnswered(true);
  };
  const handleRestart = () => {
    const newShuffled = shuffleQuestionOptions(questions);
    setShuffledQuestions(newShuffled);
    setCurrentQuestionIndex(0);
    setSelectedOption(null);
    setIsAnswered(false);
    setScore(0);
    setShowResults(false);
    setUserAnswers(new Array(questions.length).fill(null));
    setIsReviewing(false);
    setAnimatedScore(0);
    setShowRestartDialog(false);
    setIsMenuOpen(false);
  };
  const handleContinue = () => {
    setIsMenuOpen(false);
  };
  const handleSaveAndExit = () => {
    console.log('Saving progress...', {
      currentQuestionIndex,
      score,
      userAnswers
    });
    setShowExitDialog(false);
    setIsMenuOpen(false);
    alert('Progress saved! Returning to main menu...');
  };
  const handleExitWithoutSaving = () => {
    setShowExitDialog(false);
    setIsMenuOpen(false);
    handleRestart();
  };
  const handleLanguageSelect = (code: string) => {
    setSelectedLanguage(code);
    setIsLanguageOpen(false);
    setLanguageSearch('');
  };
  const exitDialogConfig: DialogConfig = {
    title: 'Exit Training?',
    description: 'Would you like to save your progress before leaving?',
    actions: [{
      label: 'Save and Exit',
      onClick: handleSaveAndExit,
      variant: 'primary'
    }, {
      label: 'Exit Without Saving',
      onClick: handleExitWithoutSaving,
      variant: 'destructive'
    }, {
      label: 'Cancel',
      onClick: () => setShowExitDialog(false),
      variant: 'cancel'
    }]
  };
  const restartDialogConfig: DialogConfig = {
    title: 'Restart Quiz?',
    description: 'Your current progress will be lost.',
    actions: [{
      label: 'Restart',
      onClick: handleRestart,
      variant: 'destructive'
    }, {
      label: 'Cancel',
      onClick: () => setShowRestartDialog(false),
      variant: 'cancel'
    }]
  };
  const tier = getPerformanceTier();
  const TierIcon = tier.icon;
  // Determine if the user's answer was correct (for feedback display)
  const isCorrect = (isAnswered || isReviewing) && userAnswers[currentQuestionIndex] === currentQuestion.correctAnswer;
  return <>
      {/* Intro overlay - renders on top while quiz paints underneath */}
      {isLoading && <div className={`fixed inset-0 bg-black flex items-center justify-center z-[200] transition-opacity duration-700 ease-out ${introFading ? 'opacity-0' : 'opacity-100'}`} data-id="element-8">
          <img src="https://cdn.magicpatterns.com/uploads/q3mHGwFqwjFEjCwMdRa798/Pixaera_Logo_White.png" alt="Pixaera" className="w-48 md:w-56 animate-in fade-in zoom-in-95 duration-1000" data-id="element-9" />
        </div>}

      <div className="min-h-screen w-full relative flex items-center justify-end p-4 md:p-8 lg:p-12 font-sans text-white antialiased selection:bg-white/30 transition-all duration-700 ease-in-out" style={{
      backgroundImage: `url(${currentQuestion.backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }} data-id="element-10">
        {showExitDialog && <ConfirmationDialog config={exitDialogConfig} onClose={() => setShowExitDialog(false)} data-id="element-11" />}
        {showRestartDialog && <ConfirmationDialog config={restartDialogConfig} onClose={() => setShowRestartDialog(false)} data-id="element-12" />}

        <div className={`fixed inset-0 bg-black/50 transition-opacity duration-300 z-40 ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => {
        setIsMenuOpen(false);
        setIsLanguageOpen(false);
        setLanguageSearch('');
      }} data-id="element-13" />

        <div className="fixed top-6 left-6 z-50" ref={menuRef} data-id="element-14">
          <button onClick={() => {
          setIsMenuOpen(!isMenuOpen);
          setIsLanguageOpen(false);
          setLanguageSearch('');
        }} className="w-11 h-11 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-xl border border-white/20 hover:bg-black/50 active:scale-95 transition-all duration-200 shadow-lg shadow-black/30" aria-label="Menu" data-id="element-15">
            {isMenuOpen ? <X className="w-5 h-5 text-white" data-id="element-16" /> : <Menu className="w-5 h-5 text-white" data-id="element-17" />}
          </button>

          {isMenuOpen && <div className="absolute top-14 left-0 w-64 backdrop-blur-2xl bg-black/70 border border-white/20 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-18">
              <div className="relative" data-id="element-19">
                <button onClick={() => {
              setIsLanguageOpen(!isLanguageOpen);
              if (!isLanguageOpen) setLanguageSearch('');
            }} className="w-full min-h-[44px] px-4 py-3 flex items-center justify-between gap-3 hover:bg-white/10 transition-colors" data-id="element-20">
                  <div className="flex items-center gap-3" data-id="element-21">
                    <Globe className="w-5 h-5 text-white/70" data-id="element-22" />
                    <span className="text-sm font-medium" data-id="element-23">Language</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-24">
                    <span className="text-sm text-white/50 truncate max-w-[80px]" data-id="element-25">
                      {languages.find(l => l.code === selectedLanguage)?.name}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${isLanguageOpen ? 'rotate-180' : ''}`} data-id="element-26" />
                  </div>
                </button>

                {isLanguageOpen && <div className="border-t border-white/10 bg-white/5" data-id="element-27">
                    <div className="p-2" data-id="element-28">
                      <div className="relative" data-id="element-29">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" data-id="element-30" />
                        <input ref={searchInputRef} type="text" value={languageSearch} onChange={e => setLanguageSearch(e.target.value)} placeholder="Search languages..." className="w-full h-10 pl-9 pr-3 bg-white/10 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 focus:bg-white/15 transition-colors" data-id="element-31" />
                      </div>
                    </div>
                    <div className="max-h-[240px] overflow-y-auto" data-id="element-32">
                      {filteredLanguages.length > 0 ? filteredLanguages.map(lang => <button key={lang.code} onClick={() => handleLanguageSelect(lang.code)} className={`w-full min-h-[44px] px-4 py-3 pl-10 flex items-center justify-between text-sm hover:bg-white/10 transition-colors ${selectedLanguage === lang.code ? 'text-white bg-white/5' : 'text-white/70'}`} data-id="element-33">
                            {lang.name}
                            {selectedLanguage === lang.code && <CheckCircle className="w-4 h-4 text-green-400" data-id="element-34" />}
                          </button>) : <div className="px-4 py-6 text-center text-sm text-white/40" data-id="element-35">
                          No languages found
                        </div>}
                    </div>
                  </div>}
              </div>

              <div className="h-px bg-white/10" data-id="element-36" />

              <button onClick={handleContinue} className="w-full min-h-[44px] px-4 py-3 flex items-center gap-3 hover:bg-white/10 transition-colors" data-id="element-37">
                <Play className="w-5 h-5 text-white/70" data-id="element-38" />
                <span className="text-sm font-medium" data-id="element-39">Continue</span>
              </button>

              <button onClick={() => setShowRestartDialog(true)} className="w-full min-h-[44px] px-4 py-3 flex items-center gap-3 hover:bg-white/10 transition-colors" data-id="element-40">
                <RefreshCw className="w-5 h-5 text-white/70" data-id="element-41" />
                <span className="text-sm font-medium" data-id="element-42">Restart</span>
              </button>

              <div className="h-px bg-white/10" data-id="element-43" />

              <button onClick={() => setShowExitDialog(true)} className="w-full min-h-[44px] px-4 py-3 flex items-center gap-3 hover:bg-white/10 transition-colors" data-id="element-44">
                <LogOut className="w-5 h-5 text-white/70" data-id="element-45" />
                <span className="text-sm font-medium" data-id="element-46">Exit</span>
              </button>
            </div>}
        </div>

        {/* Audio toggle button */}
        <button onClick={handleToggleMute} className={`fixed top-6 right-6 z-50 w-11 h-11 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-xl border border-white/20 hover:bg-black/50 active:scale-95 transition-all duration-200 shadow-lg shadow-black/30 ${isAudioPlaying ? 'ring-2 ring-white/30' : ''}`} aria-label={isMuted ? 'Unmute voiceover' : 'Mute voiceover'} data-id="element-47">
          {isMuted ? <VolumeX className="w-5 h-5 text-white/60" data-id="element-48" /> : <Volume2 className={`w-5 h-5 ${isAudioPlaying ? 'text-white' : 'text-white/60'}`} data-id="element-49" />}
        </button>

        <div className="relative w-full max-w-lg h-[600px] backdrop-blur-2xl bg-black/60 border border-white/25 rounded-[2.5rem] shadow-2xl shadow-black/50 overflow-hidden transition-all duration-500 flex flex-col" data-id="element-50">
          {!showResults && <div className="px-8 pt-8 pb-6 flex justify-end items-center border-b border-white/10 flex-shrink-0" data-id="element-51">
              <span className="text-sm font-semibold text-white/70 tracking-wide uppercase mr-4" data-id="element-52">
                {isReviewing ? 'Review' : 'Question'} {currentQuestionIndex + 1}{' '}
                of {shuffledQuestions.length}
              </span>
              <div className="h-1 w-24 bg-white/20 rounded-full overflow-hidden" data-id="element-53">
                <div className="h-full bg-white rounded-full transition-all duration-500 ease-out" style={{
              width: `${(currentQuestionIndex + 1) / shuffledQuestions.length * 100}%`
            }} data-id="element-54" />
              </div>
            </div>}

          <div ref={contentRef} className="p-8 pb-4 overflow-y-auto flex-1" data-id="element-55">
            {showResults ? <div className="text-center py-8 space-y-6 animate-in fade-in zoom-in duration-500" data-id="element-56">
                <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br ${tier.color} mb-2 ring-2 ${tier.ring} shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)]`} data-id="element-57">
                  <TierIcon className={`w-12 h-12 ${tier.iconColor}`} strokeWidth={2} data-id="element-58" />
                </div>
                <div data-id="element-59">
                  <h2 className="text-3xl font-bold mb-2 tracking-tight" data-id="element-60">
                    {tier.title}
                  </h2>
                  <p className="text-white/70 text-base leading-relaxed mb-4" data-id="element-61">
                    {tier.message}
                  </p>
                  <div className="inline-flex items-baseline gap-2 text-white/90" data-id="element-62">
                    <span className="text-5xl font-bold tabular-nums" data-id="element-63">
                      {animatedScore}
                    </span>
                    <span className="text-2xl font-medium text-white/60" data-id="element-64">
                      / {shuffledQuestions.length}
                    </span>
                    <span className="ml-2 text-lg font-semibold text-white/70" data-id="element-65">
                      ({scorePercentage}%)
                    </span>
                  </div>
                </div>
                <div className="space-y-3 pt-2" data-id="element-66">
                  <button onClick={handleReview} className="w-full min-h-[44px] py-3 px-6 bg-white/10 text-white font-semibold rounded-full hover:bg-white/15 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 border border-white/20" data-id="element-67">
                    Review Answers
                  </button>
                  <button onClick={handleRestart} className="w-full min-h-[44px] py-3 px-6 bg-white text-black font-semibold rounded-full hover:bg-white/90 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-white/10" data-id="element-68">
                    <RefreshCw className="w-5 h-5" data-id="element-69" />
                    Restart Quiz
                  </button>
                </div>
              </div> : <div className="space-y-6" data-id="element-70">
                {isReviewing && <div className="bg-blue-500/20 border border-blue-500/40 rounded-xl px-4 py-3 text-sm text-blue-100" data-id="element-71">
                    <strong data-id="element-72">Review Mode:</strong> See your answers and correct
                    solutions
                  </div>}

                <h2 className="text-2xl md:text-3xl font-bold leading-tight tracking-tight" data-id="element-73">
                  {currentQuestion.text}
                </h2>

                <div className="space-y-4" data-id="element-74">
                  {currentQuestion.options.map((option, index) => {
                const isCorrectOption = index === currentQuestion.correctAnswer;
                const isUserAnswer = userAnswers[currentQuestionIndex] === index;
                let stateClasses = 'bg-white/8 border-white/15 hover:bg-white/12';
                let icon = null;
                if (isAnswered || isReviewing) {
                  if (isUserAnswer) {
                    if (isCorrectOption) {
                      stateClasses = 'bg-green-500/25 border-green-500/50';
                      icon = <CheckCircle className="w-5 h-5 text-green-400" data-id="element-75" />;
                    } else {
                      stateClasses = 'bg-red-500/25 border-red-500/50';
                      icon = <AlertCircle className="w-5 h-5 text-red-400" data-id="element-76" />;
                    }
                  } else if (isCorrectOption) {
                    stateClasses = 'bg-green-500/15 border-green-500/35 opacity-70';
                    icon = <CheckCircle className="w-5 h-5 text-green-400/70" data-id="element-77" />;
                  } else {
                    stateClasses = 'bg-white/5 border-white/10 opacity-40';
                  }
                }
                const showInlineFeedback = (isAnswered || isReviewing) && isUserAnswer;
                return <div key={index} className={`
                          relative w-full text-left rounded-2xl border transition-all duration-300 ease-out overflow-hidden
                          ${stateClasses}
                          ${!isAnswered && !isReviewing ? 'hover:scale-[1.01] active:scale-[0.99] cursor-pointer' : ''}
                        `} onClick={() => handleOptionSelect(index)} role="button" tabIndex={0} data-id="element-78">
                        <div className="flex items-center gap-4 min-h-[44px] px-4 py-3" data-id="element-79">
                          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center" data-id="element-80">
                            {(isAnswered || isReviewing) && (isUserAnswer || isCorrectOption) ? icon : <span className="text-sm font-semibold text-white/60" data-id="element-81">
                                {index + 1}
                              </span>}
                          </div>
                          <span className="font-normal text-base leading-[1.6]" data-id="element-82">
                            {option}
                          </span>
                        </div>

                        {showInlineFeedback && <div className="px-4 pb-3 pt-0 animate-in fade-in slide-in-from-top-1 duration-300" data-id="element-83">
                            <div className={`
                                mt-1 pt-3 border-t text-sm leading-relaxed font-medium
                                ${isCorrectOption ? 'border-green-500/30 text-green-100' : 'border-red-500/30 text-red-100'}
                              `} data-id="element-84">
                              {isCorrectOption ? currentQuestion.correctMessage : currentQuestion.incorrectMessage}
                            </div>
                          </div>}
                      </div>;
              })}
                </div>
              </div>}
          </div>

          {!showResults && isAnswered && <div className="px-8 py-4 border-t border-white/10 flex-shrink-0" data-id="element-85">
              <div className="flex justify-between items-center" data-id="element-86">
                {isReviewing && currentQuestionIndex > 0 ? <button onClick={handlePrevious} className="group flex items-center gap-2 min-h-[44px] pl-5 pr-6 py-3 bg-white/10 text-white font-semibold rounded-full hover:bg-white/15 active:scale-[0.98] transition-all border border-white/20" data-id="element-87">
                    <ChevronRight className="w-5 h-5 rotate-180 transition-transform group-hover:-translate-x-1" data-id="element-88" />
                    Previous
                  </button> : <div data-id="element-89" />}

                <button onClick={handleNext} className="group flex items-center gap-2 min-h-[44px] pl-6 pr-5 py-3 bg-white text-black font-semibold rounded-full hover:bg-white/90 active:scale-[0.98] transition-all shadow-lg shadow-white/10 ml-auto" data-id="element-90">
                  {currentQuestionIndex === shuffledQuestions.length - 1 ? isReviewing ? 'Back to Results' : 'Finish' : 'Next Question'}
                  <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-91" />
                </button>
              </div>
            </div>}
        </div>
      </div>
    </>;
}